// Layer 1 data (ingested, not authored): structured live rules for Paul's V8
// Met Diagnostics register — rule-set stream slice 1 (#297 / paul-fmea.A5).
// One `DiagnosticRule` per register cause that can carry a `when[]` clause
// against seeded plant signals; gaps and causes already named by seed or
// advisory rules are excluded here (see `MET_DIAGNOSTICS_GAP_REASONS` and the
// file header on `bunker-hill-met-diagnostics-register.ts`).

import type { DiagnosticRule, RulePriority } from '@/versions/v1/schema/diagnostic-rule';
import type { AffectsFlag, MetDiagnosticsSection } from '@/versions/v1/schema/met-diagnostics-cause';
import { MET_DIAGNOSTICS_CAUSES } from '@/versions/v1/shared/bunker-hill-met-diagnostics-register';

type CauseRow = {
  symptomGroup: MetDiagnosticsSection;
  affects: readonly AffectsFlag[];
};

/** Look up register row fields every live rule must copy verbatim. */
export function causeRow(id: string): CauseRow {
  const row = MET_DIAGNOSTICS_CAUSES.find((cause) => cause.id === id);
  if (!row) {
    throw new Error(`Unknown Met Diagnostics cause: ${id}`);
  }
  return { symptomGroup: row.section, affects: row.affects };
}

function priorityFor(id: string): RulePriority {
  const row = MET_DIAGNOSTICS_CAUSES.find((cause) => cause.id === id);
  const workbookPriority = row?.priority ?? 3;
  if (workbookPriority <= 1) return 'P1';
  if (workbookPriority === 2) return 'P2';
  if (workbookPriority === 3) return 'P3';
  return 'P4';
}

/** Honest gaps — no continuous or shift signal exists for a live `when[]`. */
export const MET_DIAGNOSTICS_GAP_REASONS: Record<string, string> = {
  'MD-010':
    'Oxidised/weathered Pb minerals — collector response needs a mineralogy assay, not an on-line signal.',
  'MD-016':
    'Filter cake moisture dilutes analytical grade — a filtration/sampling check, not a flotation instrument.',
  'MD-018':
    'Coarse native silver/argentite liberation — a feed mineralogy result, not a continuous signal.',
  'MD-026':
    'Oxidised Zn minerals (smithsonite, hemimorphite) — a ROM mineralogy assay, not an on-line signal.',
  'MD-042':
    'Differential grindability — sulphide liberation needs a size-by-assay panel, not bulk P80 alone.',
  'MD-047':
    'Slime/clay coating on mineral surfaces — a mineralogy/microscopy check, not a plant instrument.',
  'MD-055':
    'Bismuth via galena lattice or discrete bismuthinite — inherent in the ore, not removable on-line.',
  'MD-057':
    'Cadmium isomorphic substitution — scales with Zn grade in the ore, not a process variable.',
};

export const MET_DIAGNOSTICS_SECTION_RULES: readonly DiagnosticRule[] = [
  // --- Pb Recovery Below Target (MD-002, MD-003, MD-005, MD-008, MD-009) ---
  // MD-001 → dr-media / dr-hardness; MD-004 → dr-ag-3418a; MD-006 → dr-air-flow-pb;
  // MD-007 → dr-dart-valve; MD-010 → gap.
  {
    id: 'dr-pb-ph-high',
    signalPattern: 'AT-128 Pb rougher pH above 8.5 + Pb recovery falling — over-liming activates Zn',
    diagnosis: 'Pb rougher pH too high — over-liming activates Zn',
    priority: priorityFor('MD-002'),
    responsible: 'metallurgist',
    recommendedAction:
      'Trim lime dosing to bring AT-128 back into the 7.5–8.5 band; check Zn reporting to Pb concentrate before chasing collector.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-ph', condition: 'above-band', label: 'AT-128 above 8.5' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    rival: 'dr-pb-ph-low',
    discriminator: {
      signal: 'pb-con-zn',
      compare: '>',
      threshold: 4.5,
      evidenceKind: 'lab-sample',
      label: 'Pb concentrate Zn assay — activated Zn confirms over-liming',
    },
    ...causeRow('MD-002'),
    sourceCauseId: ['MD-002'],
  },
  {
    id: 'dr-pb-ph-low',
    signalPattern: 'AT-128 Pb rougher pH below 7.5 + Pb recovery falling — lime supply failure',
    diagnosis: 'Pb rougher pH too low — lime supply failure',
    priority: priorityFor('MD-003'),
    responsible: 'operator',
    recommendedAction:
      'Restore lime feed to the Pb circuit — verify silo level and dosing pump before adjusting collector.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-ph', condition: 'below-band', label: 'AT-128 below 7.5' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    rival: 'dr-pb-ph-high',
    discriminator: {
      signal: 'reagent-lime-rate',
      compare: '<',
      threshold: 80,
      evidenceKind: 'visual',
      label: 'Lime dosing rate check — pump off or silo empty confirms supply failure',
    },
    ...causeRow('MD-003'),
    sourceCauseId: ['MD-003'],
  },
  {
    id: 'dr-zn-depress-fail',
    signalPattern: 'ZnSO₄/SMBS depression below design + Zn loading Pb rougher concentrate',
    diagnosis: 'Insufficient Zn depression — Zn overloads Pb froth',
    priority: priorityFor('MD-005'),
    responsible: 'metallurgist',
    recommendedAction:
      'Restore Zn depressant dosing to design; assay Pb rougher concentrate for Zn before increasing collector.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'reagent-znso4-rate', condition: 'below-band', label: 'ZnSO₄ below design' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'pb-rougher-ph', condition: 'stable', label: 'Pb rougher pH steady' },
    ],
    rival: 'dr-pb-ph-high',
    discriminator: {
      signal: 'pb-rougher-con-zn',
      compare: '>',
      threshold: 6,
      evidenceKind: 'lab-sample',
      label: 'Pb rougher concentrate Zn assay — elevated Zn confirms depression failure',
    },
    ...causeRow('MD-005'),
    sourceCauseId: ['MD-005'],
  },
  {
    id: 'dr-ore-hardness-step',
    signalPattern: 'JT-023 kW/tph rising + head SiO₂ rising + PSA coarsening — harder ore, not media wear',
    diagnosis: 'Ore hardness increase — coarser grind, same energy',
    priority: priorityFor('MD-008'),
    responsible: 'metallurgist',
    recommendedAction:
      'Reduce feed rate until P80 recovers; advise mine planner of the hardness step-change.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'mill-power-ratio', condition: 'rising', label: 'JT-023 kW/tph rising' },
      { signal: 'head-sio2', condition: 'rising', label: 'Head SiO₂ rising' },
      { signal: 'grind-p80', condition: 'rising', label: 'SA01 P80 rising' },
    ],
    rival: 'dr-hardness',
    discriminator: {
      signal: 'head-pb-grade',
      compare: '<',
      threshold: 2.8,
      evidenceKind: 'lab-sample',
      label: 'ROM Pb head grade stable — rules out head-grade decline as the driver',
    },
    ...causeRow('MD-008'),
    sourceCauseId: ['MD-008'],
  },
  {
    id: 'dr-pb-head-decline',
    signalPattern: 'Head Pb grade falling + Pb recovery falling with grind and pH on target',
    diagnosis: 'Pb head grade decline — less mineralisation in feed',
    priority: priorityFor('MD-009'),
    responsible: 'superintendent',
    recommendedAction:
      'Request ROM Pb assays and mine advance plan — do not chase reagents while head grade is the limiter.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'head-pb-grade', condition: 'falling', label: 'Head Pb grade falling' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'grind-p80', condition: 'stable', label: 'SA01 P80 steady' },
    ],
    rival: 'dr-ore-hardness-step',
    discriminator: {
      signal: 'mill-power-ratio',
      compare: '<=',
      threshold: 14,
      evidenceKind: 'lab-sample',
      label: 'kW/tph steady — rules out a hardness-driven grind failure',
    },
    ...causeRow('MD-009'),
    sourceCauseId: ['MD-009'],
  },

  // --- Pb Con Grade Below 67% Pb (MD-011–MD-015); MD-016 → gap ---
  {
    id: 'dr-pb-con-zn-contam',
    signalPattern: 'Pb concentrate Zn rising + Pb grade below 67% — Zn activation not depressed',
    diagnosis: 'Zn contamination — activation not depressed',
    priority: priorityFor('MD-011'),
    responsible: 'metallurgist',
    recommendedAction:
      'Increase Zn depression and verify Pb rougher pH is not over-liming; re-assay Pb concentrate.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-con-zn', condition: 'rising', label: 'Pb concentrate Zn rising' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-pb-fe-contam',
    discriminator: {
      signal: 'pb-rougher-ph',
      compare: '>',
      threshold: 8.5,
      evidenceKind: 'lab-sample',
      label: 'Pb rougher pH check — high pH confirms Zn activation path',
    },
    ...causeRow('MD-011'),
    sourceCauseId: ['MD-011'],
  },
  {
    id: 'dr-pb-fe-contam',
    signalPattern: 'Pb concentrate grade falling + pyrite selectivity loss — iron reporting to Pb conc',
    diagnosis: 'Iron/pyrite contamination — insufficient selectivity',
    priority: priorityFor('MD-012'),
    responsible: 'metallurgist',
    recommendedAction:
      'Tighten pH and depressant strategy; request Fe/penalty-element assay on Pb concentrate.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
      { signal: 'pb-rougher-ph', condition: 'below-band', label: 'Pb rougher pH below target band' },
    ],
    rival: 'dr-pb-con-zn-contam',
    discriminator: {
      signal: 'head-as',
      compare: '>',
      threshold: 0.35,
      evidenceKind: 'lab-sample',
      label: 'Head As assay — elevated arsenopyrite/pyrite association in feed',
    },
    ...causeRow('MD-012'),
    sourceCauseId: ['MD-012'],
  },
  {
    id: 'dr-pb-silica-entrain',
    signalPattern: 'Pb concentrate grade falling + froth too stable — silica/gangue entrainment',
    diagnosis: 'Silica/gangue entrainment — froth too stable',
    priority: priorityFor('MD-013'),
    responsible: 'operator',
    recommendedAction:
      'Reduce frother or trim air to break froth stability; check froth depth and launder level.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-froth', condition: 'above-band', label: 'FR-PB01 froth depth above band' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-pb-fe-contam',
    discriminator: {
      signal: 'head-sio2',
      compare: '>',
      threshold: 12,
      evidenceKind: 'lab-sample',
      label: 'Head SiO₂ assay — gangue-rich feed confirms entrainment risk',
    },
    ...causeRow('MD-013'),
    sourceCauseId: ['MD-013'],
  },
  {
    id: 'dr-regrind-coarse',
    signalPattern: 'Regrind P80 above target + Pb cleaner grade suffering — poor liberation',
    diagnosis: 'Regrind P80 too coarse — poor cleaner liberation',
    priority: priorityFor('MD-014'),
    responsible: 'metallurgist',
    recommendedAction:
      'Verify regrind cyclone configuration and feed density; target 30 µm before cleaner adjustments.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'above-band', label: 'Regrind/cleaner feed P80 above band' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-pb-cleaner-upset',
    discriminator: {
      signal: 'pb-recovery',
      compare: '<',
      threshold: 82,
      evidenceKind: 'lab-sample',
      label: 'Pb recovery check — liberation-limited grade with recovery still acceptable',
    },
    ...causeRow('MD-014'),
    sourceCauseId: ['MD-014'],
  },
  {
    id: 'dr-pb-cleaner-upset',
    signalPattern: 'Pb cleaner circulating load excursion + grade/recovery swing — LT-155/156 instability',
    diagnosis: 'Pb cleaner circuit upset — LT-155/156 instability',
    priority: priorityFor('MD-015'),
    responsible: 'operator',
    recommendedAction:
      'Stabilise cleaner levels and recycle flows before touching regrind or rougher setpoints.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-level', condition: 'excursion', label: 'Pb rougher/cleaner level excursion' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-regrind-coarse',
    discriminator: {
      signal: 'pb-recovery',
      compare: '>',
      threshold: 80,
      evidenceKind: 'visual',
      label: 'Cleaner launder inspection — unstable level or froth carry-over visible',
    },
    ...causeRow('MD-015'),
    sourceCauseId: ['MD-015'],
  },

  // --- Ag Recovery Below Target (MD-017, MD-019); MD-018 → gap ---
  {
    id: 'dr-ag-pb-rec-coupling',
    signalPattern: 'Pb recovery falling + Ag recovery falling together — Ag travels with Pb sulphides',
    diagnosis: 'Pb recovery failure — Ag travels with Pb sulphides',
    priority: priorityFor('MD-017'),
    responsible: 'metallurgist',
    recommendedAction:
      'Fix the Pb rougher recovery path first — Ag will not recover independently while Pb is lost.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'ag-recovery', condition: 'falling', label: 'Ag recovery falling' },
    ],
    rival: 'dr-ag-cleaner-loss',
    discriminator: {
      signal: 'pb-rougher-ph',
      compare: '<=',
      threshold: 8.5,
      evidenceKind: 'lab-sample',
      label: 'Pb rougher pH in band — confirms coupled Pb/Ag loss, not a cleaner-only issue',
    },
    ...causeRow('MD-017'),
    sourceCauseId: ['MD-017'],
  },
  {
    id: 'dr-ag-cleaner-loss',
    signalPattern: 'Ag recovery falling with Pb recovery steady — Ag reporting to Pb cleaner tails',
    diagnosis: 'Ag reporting to Pb cleaner tails — insufficient cleaning',
    priority: priorityFor('MD-019'),
    responsible: 'metallurgist',
    recommendedAction:
      'Inspect Pb cleaner mass pull and recycle; request Ag assay on cleaner tails.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'ag-recovery', condition: 'falling', label: 'Ag recovery falling' },
      { signal: 'pb-recovery', condition: 'stable', label: 'Pb recovery steady' },
    ],
    rival: 'dr-ag-pb-rec-coupling',
    discriminator: {
      signal: 'ag-con-grade',
      compare: '<',
      threshold: 900,
      evidenceKind: 'lab-sample',
      label: 'Pb/Ag concentrate Ag grade — depressed Ag confirms cleaner loss',
    },
    ...causeRow('MD-019'),
    sourceCauseId: ['MD-019'],
  },

  // --- Zn Recovery Below Target (MD-021, MD-022, MD-023, MD-024, MD-025) ---
  // MD-020 → dr-cuso4; MD-021/025 → ADVISORY_DEPTH_PATTERN_RULES; MD-023 → dr-air-flow-zn; MD-026 → gap.
  {
    id: 'dr-mibc-fail',
    signalPattern: 'MIBC frother below design + Zn rougher froth collapsing + Zn recovery falling',
    diagnosis: 'MIBC frother failure — froth collapses in Zn roughers',
    priority: priorityFor('MD-022'),
    responsible: 'operator',
    recommendedAction:
      'Restore MIBC dosing and verify frother line — confirm froth depth before increasing CuSO₄.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'reagent-mibc-rate', condition: 'below-band', label: 'MIBC below design' },
      { signal: 'zn-rougher-froth', condition: 'below-band', label: 'Zn rougher froth depth below band' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-zn-grind-coarse',
    discriminator: {
      signal: 'zn-rougher-air',
      compare: '<=',
      threshold: 16,
      evidenceKind: 'visual',
      label: 'Zn rougher air steady — rules out air-driven froth collapse',
    },
    ...causeRow('MD-022'),
    sourceCauseId: ['MD-022'],
  },
  {
    id: 'dr-zn-grind-coarse',
    signalPattern: 'SA01 P80 above band + Zn recovery falling — Zn minerals not liberated',
    diagnosis: 'Grind too coarse for Zn mineral liberation',
    priority: priorityFor('MD-024'),
    responsible: 'metallurgist',
    recommendedAction:
      'Cut feed until P80 recovers inside band; verify cyclone configuration before Zn reagent changes.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'above-band', label: 'SA01 P80 above band' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-mibc-fail',
    discriminator: {
      signal: 'zn-conditioner-ph',
      compare: '<=',
      threshold: 12.5,
      evidenceKind: 'lab-sample',
      label: 'Zn float pH in band — rules out activation/pH as the primary limiter',
    },
    ...causeRow('MD-024'),
    sourceCauseId: ['MD-024'],
  },

  // --- Zn Con Grade Below Spec (MD-027–MD-029) ---
  {
    id: 'dr-zn-pb-contam',
    signalPattern: 'Zn concentrate grade falling — insufficient Pb depression in Zn circuit',
    diagnosis: 'Pb contamination — insufficient Pb depression',
    priority: priorityFor('MD-027'),
    responsible: 'metallurgist',
    recommendedAction:
      'Increase Pb depression in the Zn circuit and verify SMBS carry-over from Pb is not masking lime.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-con-grade', condition: 'falling', label: 'Zn concentrate grade falling' },
      { signal: 'reagent-smbs-rate', condition: 'below-band', label: 'SMBS below design' },
    ],
    rival: 'dr-zn-fe-contam',
    discriminator: {
      signal: 'pb-rougher-con-pb',
      compare: '>',
      threshold: 25,
      evidenceKind: 'lab-sample',
      label: 'Zn rougher concentrate Pb assay — elevated Pb confirms depression failure',
    },
    ...causeRow('MD-027'),
    sourceCauseId: ['MD-027'],
  },
  {
    id: 'dr-zn-fe-contam',
    signalPattern: 'Zn concentrate grade falling + pyrite reporting — iron contamination',
    diagnosis: 'Iron/pyrite contamination in Zn concentrate',
    priority: priorityFor('MD-028'),
    responsible: 'metallurgist',
    recommendedAction:
      'Tighten Zn float pH and depressant strategy; request penalty-element panel on Zn concentrate.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-con-grade', condition: 'falling', label: 'Zn concentrate grade falling' },
      { signal: 'zn-conditioner-ph', condition: 'below-band', label: 'Zn float pH below band' },
    ],
    rival: 'dr-zn-pb-contam',
    discriminator: {
      signal: 'head-as',
      compare: '>',
      threshold: 0.35,
      evidenceKind: 'lab-sample',
      label: 'Head As assay — pyrite/arsenopyrite association in feed',
    },
    ...causeRow('MD-028'),
    sourceCauseId: ['MD-028'],
  },
  {
    id: 'dr-zn-silica-entrain',
    signalPattern: 'Zn concentrate grade falling + froth too stable — gangue entrainment',
    diagnosis: 'Silica/gangue entrainment — excessive froth stability',
    priority: priorityFor('MD-029'),
    responsible: 'operator',
    recommendedAction:
      'Reduce frother or trim Zn rougher air to break froth stability; check launder levels.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-rougher-froth', condition: 'above-band', label: 'Zn rougher froth depth above band' },
      { signal: 'zn-con-grade', condition: 'falling', label: 'Zn concentrate grade falling' },
    ],
    rival: 'dr-zn-fe-contam',
    discriminator: {
      signal: 'head-sio2',
      compare: '>',
      threshold: 12,
      evidenceKind: 'lab-sample',
      label: 'Head SiO₂ assay — gangue-rich feed confirms entrainment',
    },
    ...causeRow('MD-029'),
    sourceCauseId: ['MD-029'],
  },

  // --- Both Pb + Zn Recovery Declining (MD-031, MD-032) ---
  // MD-030 → dr-grind-fail; MD-033 → dr-mineralogy; MD-034 → dr-throughput.
  {
    id: 'dr-lime-silo-empty',
    signalPattern: 'Lime rate collapsed + Pb and Zn float pH falling together',
    diagnosis: 'Lime silo empty — pH drops in all circuits together',
    priority: priorityFor('MD-031'),
    responsible: 'operator',
    recommendedAction:
      'Restore lime feed to all circuits — verify silo level and dosing pumps before any reagent chase.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'reagent-lime-rate', condition: 'below-band', label: 'Lime dosing below design' },
      { signal: 'pb-rougher-ph', condition: 'falling', label: 'Pb rougher pH falling' },
      { signal: 'zn-conditioner-ph', condition: 'falling', label: 'Zn float pH falling' },
    ],
    rival: 'dr-process-water',
    discriminator: {
      signal: 'grind-ph',
      compare: '<',
      threshold: 8,
      evidenceKind: 'visual',
      label: 'Grind-circuit pH check — global acidification confirms lime outage',
    },
    ...causeRow('MD-031'),
    sourceCauseId: ['MD-031'],
  },
  {
    id: 'dr-process-water',
    signalPattern: 'Both recoveries falling + grind pH drifting — process water chemistry upset',
    diagnosis: 'Process water chemistry upset — contamination or high DO',
    priority: priorityFor('MD-032'),
    responsible: 'metallurgist',
    recommendedAction:
      'Sample process water for DO, hardness and metal ions; bleed fresh water if recycle is contaminated.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-ph', condition: 'excursion', label: 'Grind-circuit pH excursion' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-lime-silo-empty',
    discriminator: {
      signal: 'reagent-lime-rate',
      compare: '>=',
      threshold: 80,
      evidenceKind: 'lab-sample',
      label: 'Lime dosing on design — rules out silo empty as the co-cause',
    },
    ...causeRow('MD-032'),
    sourceCauseId: ['MD-032'],
  },

  // --- Tailings Grades Elevated (MD-035–MD-038) ---
  {
    id: 'dr-pb-rougher-rec-fail',
    signalPattern: 'Pb recovery excursion low + tails Pb implied high — all Pb to tails',
    diagnosis: 'Pb rougher recovery failure — all Pb to tails',
    priority: priorityFor('MD-035'),
    responsible: 'metallurgist',
    recommendedAction:
      'Walk the Pb rougher circuit: air, level, pH and collector before accepting a tails assay spike.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-recovery', condition: 'excursion', label: 'Pb recovery excursion' },
      { signal: 'pb-rougher-air', condition: 'stable', label: 'Pb rougher air steady' },
    ],
    rival: 'dr-zn-rougher-rec-fail',
    discriminator: {
      signal: 'pb-rougher-ph',
      compare: '<',
      threshold: 7.5,
      evidenceKind: 'lab-sample',
      label: 'Pb rougher pH assay — off-band pH explains mass pull loss',
    },
    ...causeRow('MD-035'),
    sourceCauseId: ['MD-035'],
  },
  {
    id: 'dr-zn-rougher-rec-fail',
    signalPattern: 'Zn recovery excursion low + tails Zn implied high — all Zn to tails',
    diagnosis: 'Zn rougher recovery failure — all Zn to tails',
    priority: priorityFor('MD-036'),
    responsible: 'metallurgist',
    recommendedAction:
      'Walk the Zn rougher circuit: CuSO₄, pH, frother and air before accepting a tails assay spike.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-recovery', condition: 'excursion', label: 'Zn recovery excursion' },
      { signal: 'zn-rougher-air', condition: 'stable', label: 'Zn rougher air steady' },
    ],
    rival: 'dr-pb-rougher-rec-fail',
    discriminator: {
      signal: 'zn-conditioner-ph',
      compare: '<',
      threshold: 10.5,
      evidenceKind: 'lab-sample',
      label: 'Zn float pH assay — activation failure explains tails loss',
    },
    ...causeRow('MD-036'),
    sourceCauseId: ['MD-036'],
  },
  {
    id: 'dr-assay-spike',
    signalPattern: 'Single-shift tails grade spike with recoveries steady — sampling or assay error',
    diagnosis: 'Sampling or assay error — single-shift spike',
    priority: priorityFor('MD-037'),
    responsible: 'metallurgist',
    recommendedAction:
      'Repeat tails assay and check sampler cutter condition before opening a process investigation.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'head-pb-grade', condition: 'excursion', label: 'Head Pb grade single-shift excursion' },
      { signal: 'pb-recovery', condition: 'stable', label: 'Pb recovery steady' },
      { signal: 'zn-recovery', condition: 'stable', label: 'Zn recovery steady' },
    ],
    rival: 'dr-pb-rougher-rec-fail',
    discriminator: {
      signal: 'head-pb-grade',
      compare: '<=',
      threshold: 3.2,
      evidenceKind: 'lab-sample',
      label: 'Duplicate tails sample — spike not reproduced confirms assay error',
    },
    ...causeRow('MD-037'),
    sourceCauseId: ['MD-037'],
  },
  {
    id: 'dr-tails-thickener-sc',
    signalPattern: 'Both recoveries falling + slurry flow excursion — tails thickener short-circuit',
    diagnosis: 'Tails thickener short-circuiting — metals in overflow',
    priority: priorityFor('MD-038'),
    responsible: 'operator',
    recommendedAction:
      'Inspect tails thickener overflow clarity and rake; divert overflow to containment until stable.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'feed-slurry-flow', condition: 'excursion', label: 'Flotation feed slurry flow excursion' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-assay-spike',
    discriminator: {
      signal: 'cyclone-overflow-density',
      compare: '<',
      threshold: 28,
      evidenceKind: 'visual',
      label: 'Thickener overflow visual — cloudy overflow confirms short-circuit',
    },
    ...causeRow('MD-038'),
    sourceCauseId: ['MD-038'],
  },

  // --- Additional Grinding Circuit Root Causes (MD-039, MD-041, MD-043) ---
  // MD-040 → dr-media-size; MD-042 → gap.
  {
    id: 'dr-overgrind',
    signalPattern: 'SA01 P80 below band + both recoveries falling — excess fines/slimes',
    diagnosis: 'Overgrinding — excess fines/slimes reduce selectivity',
    priority: priorityFor('MD-039'),
    responsible: 'metallurgist',
    recommendedAction:
      'Coarsen cyclone cut and reduce mill power — target bulk P80 back inside band before reagent changes.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-p80', condition: 'below-band', label: 'SA01 P80 below band' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-cyclone-density-err',
    discriminator: {
      signal: 'mill-power-ratio',
      compare: '>',
      threshold: 15,
      evidenceKind: 'lab-sample',
      label: 'kW/tph elevated — confirms energy spent on fines generation',
    },
    ...causeRow('MD-039'),
    sourceCauseId: ['MD-039'],
  },
  {
    id: 'dr-cyclone-density-err',
    signalPattern: 'Cyclone O/F density excursion + P80 unstable — dilution water control error',
    diagnosis: 'Cyclone feed density/dilution water control error',
    priority: priorityFor('MD-041'),
    responsible: 'operator',
    recommendedAction:
      'Restore cyclone feed % solids to design; verify dilution water valve and DT-204 instrument.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'cyclone-overflow-density', condition: 'excursion', label: 'DT-204 cyclone O/F density excursion' },
      { signal: 'grind-p80', condition: 'rising', label: 'SA01 P80 rising' },
    ],
    rival: 'dr-overgrind',
    discriminator: {
      signal: 'mill-feed-rate',
      compare: '<=',
      threshold: 80,
      evidenceKind: 'visual',
      label: 'Feed rate steady — rules out throughput exceedance as the density driver',
    },
    ...causeRow('MD-041'),
    sourceCauseId: ['MD-041'],
  },
  {
    id: 'dr-regrind-bypass',
    signalPattern: 'Pb concentrate grade falling with bulk P80 on target — regrind bypass',
    diagnosis: 'Regrind circuit bypass or CycloPac malfunction',
    priority: priorityFor('MD-043'),
    responsible: 'maintenance',
    recommendedAction:
      'Verify regrind stream is online and CycloPac valves are not bypassed; inspect regrind cyclone apex.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
      { signal: 'grind-p80', condition: 'stable', label: 'Bulk SA01 P80 steady' },
    ],
    rival: 'dr-cyclone-density-err',
    discriminator: {
      signal: 'pb-recovery',
      compare: '>',
      threshold: 80,
      evidenceKind: 'visual',
      label: 'Regrind line flow check — bypass valve or pump off confirms malfunction',
    },
    ...causeRow('MD-043'),
    sourceCauseId: ['MD-043'],
  },

  // --- Additional Flotation Circuit Root Causes (MD-044–MD-052); MD-047 → gap ---
  {
    id: 'dr-ultrafine-loss',
    signalPattern: 'Both recoveries falling + P80 on target — ultra-fine particle losses',
    diagnosis: 'Ultra-fine particle recovery loss — sub-10 µm not recovered',
    priority: priorityFor('MD-044'),
    responsible: 'metallurgist',
    recommendedAction:
      'Request size-by-assay on tails; consider frother/collector strategy for fines before coarsening grind.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'grind-p80', condition: 'stable', label: 'SA01 P80 steady' },
    ],
    rival: 'dr-coarse-composite',
    discriminator: {
      signal: 'reagent-mibc-rate',
      compare: '<',
      threshold: 18,
      evidenceKind: 'lab-sample',
      label: 'Fines fraction assay — elevated -10 µm in tails confirms ultra-fine loss',
    },
    ...causeRow('MD-044'),
    sourceCauseId: ['MD-044'],
  },
  {
    id: 'dr-coarse-composite',
    signalPattern: 'Both recoveries falling + P80 coarse but not global failure — composite particles',
    diagnosis: 'Coarse composite recovery loss — too heavy for bubble lift-off',
    priority: priorityFor('MD-045'),
    responsible: 'metallurgist',
    recommendedAction:
      'Target regrind/cleaner liberation for composite particles; verify air and froth depth support mass pull.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'grind-p80', condition: 'above-band', label: 'SA01 P80 above band' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    rival: 'dr-ultrafine-loss',
    discriminator: {
      signal: 'pb-rougher-air',
      compare: '>=',
      threshold: 12,
      evidenceKind: 'lab-sample',
      label: 'Composite particle microscope check — locked grains confirm coarse composite loss',
    },
    ...causeRow('MD-045'),
    sourceCauseId: ['MD-045'],
  },
  {
    id: 'dr-recycle-water',
    signalPattern: 'Both recoveries drifting down + grind pH stable — residual reagents in recycle',
    diagnosis: 'Recycle water carrying residual reagents/metal ions',
    priority: priorityFor('MD-046'),
    responsible: 'metallurgist',
    recommendedAction:
      'Bleed fresh water into the grinding circuit and sample recycle for Cu/Fe ions before reagent changes.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
      { signal: 'grind-ph', condition: 'stable', label: 'Grind-circuit pH steady' },
    ],
    rival: 'dr-floatable-gangue',
    discriminator: {
      signal: 'grind-ph',
      compare: '<=',
      threshold: 9.5,
      evidenceKind: 'lab-sample',
      label: 'Process water metal-ion panel — elevated Cu/Fe confirms recycle contamination',
    },
    ...causeRow('MD-046'),
    sourceCauseId: ['MD-046'],
  },
  {
    id: 'dr-floatable-gangue',
    signalPattern: 'Both concentrate grades falling slowly — naturally floatable gangue diluting grade',
    diagnosis: 'Naturally floatable gangue (talc, carbon, sericite) dilutes grade',
    priority: priorityFor('MD-048'),
    responsible: 'superintendent',
    recommendedAction:
      'Request mineralogy on ROM and concentrates; consider gangue depressant trial before mine advance.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
      { signal: 'zn-con-grade', condition: 'falling', label: 'Zn concentrate grade falling' },
      { signal: 'grind-p80', condition: 'stable', label: 'SA01 P80 steady' },
    ],
    rival: 'dr-recycle-water',
    discriminator: {
      signal: 'head-sio2',
      compare: '>',
      threshold: 14,
      evidenceKind: 'lab-sample',
      label: 'ROM mineralogy — talc/carbon/sericite confirms floatable gangue',
    },
    ...causeRow('MD-048'),
    sourceCauseId: ['MD-048'],
  },
  {
    id: 'dr-launder-imbalance',
    signalPattern: 'Bank feed imbalance — residence time shortened on one side',
    diagnosis: 'Bank feed/launder imbalance shortens cell residence time',
    priority: priorityFor('MD-049'),
    responsible: 'operator',
    recommendedAction:
      'Balance launder splits and check for plugged or overflowing launders before reagent changes.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-level', condition: 'excursion', label: 'Pb rougher level excursion' },
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
    ],
    rival: 'dr-impeller-wear',
    discriminator: {
      signal: 'pb-rougher-density',
      compare: '>',
      threshold: 32,
      evidenceKind: 'visual',
      label: 'Launder walk-down — uneven froth transport confirms imbalance',
    },
    ...causeRow('MD-049'),
    sourceCauseId: ['MD-049'],
  },
  {
    id: 'dr-impeller-wear',
    signalPattern: 'Air on setpoint + recovery falling — bubble size distribution shifted by wear',
    diagnosis: 'Impeller/rotor-stator wear alters bubble size distribution',
    priority: priorityFor('MD-050'),
    responsible: 'maintenance',
    recommendedAction:
      'Schedule cell inspection for rotor-stator wear; compare froth texture bank-to-bank.',
    causeCategory: 'mechanical-electrical',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-recovery', condition: 'falling', label: 'Pb recovery falling' },
      { signal: 'pb-rougher-air', condition: 'stable', label: 'FI-403 air on setpoint' },
    ],
    rival: 'dr-launder-imbalance',
    discriminator: {
      signal: 'pb-rougher-froth',
      compare: '<',
      threshold: 140,
      evidenceKind: 'visual',
      label: 'Cell inspection — worn impeller and fine bubbles confirm mechanical wear',
    },
    ...causeRow('MD-050'),
    sourceCauseId: ['MD-050'],
  },
  {
    id: 'dr-middlings-build',
    signalPattern: 'Pb cleaner recycle building + grade/recovery swing — middlings trapped',
    diagnosis: 'Middlings build-up between cleaner and recleaner stages',
    priority: priorityFor('MD-051'),
    responsible: 'operator',
    recommendedAction:
      'Flush cleaner/recleaner middlings inventory and rebalance circulating loads before setpoint changes.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'pb-rougher-level', condition: 'rising', label: 'Pb cleaner level rising' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-reagent-sequence',
    discriminator: {
      signal: 'pb-recovery',
      compare: '<',
      threshold: 83,
      evidenceKind: 'visual',
      label: 'Cleaner box inspection — middlings inventory visible confirms build-up',
    },
    ...causeRow('MD-051'),
    sourceCauseId: ['MD-051'],
  },
  {
    id: 'dr-reagent-sequence',
    signalPattern: 'Collector dose normal + depression ineffective — depressant added after collector',
    diagnosis: 'Reagent sequencing error — depressant added after collector',
    priority: priorityFor('MD-052'),
    responsible: 'operator',
    recommendedAction:
      'Verify reagent addition order at the conditioner — reset sequence before increasing doses.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'reagent-smbs-rate', condition: 'excursion', label: 'SMBS dosing excursion' },
      { signal: 'reagent-3418a-rate', condition: 'stable', label: '3418A collector on setpoint' },
      { signal: 'pb-con-grade', condition: 'falling', label: 'Pb concentrate grade falling' },
    ],
    rival: 'dr-middlings-build',
    discriminator: {
      signal: 'pb-rougher-con-zn',
      compare: '>',
      threshold: 5,
      evidenceKind: 'visual',
      label: 'Conditioner walk-down — depressant line downstream of collector confirms sequencing error',
    },
    ...causeRow('MD-052'),
    sourceCauseId: ['MD-052'],
  },

  // --- Concentrate Penalty Elements / Impurities (MD-053, MD-056–MD-060) ---
  // MD-054 → dr-sb-bi-depression; MD-055, MD-057 → gap.
  {
    id: 'dr-tetrahedrite-float',
    signalPattern: 'Penalty-element risk rising with Pb recovery steady — sulfosalt floats with galena',
    diagnosis: 'Tetrahedrite-tennantite sulfosalt floats with galena',
    priority: priorityFor('MD-053'),
    responsible: 'metallurgist',
    recommendedAction:
      'Request Sb/As penalty panel on Pb concentrate; trial depression before smelter dispatch.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'head-as', condition: 'rising', label: 'Head As rising' },
      { signal: 'pb-recovery', condition: 'stable', label: 'Pb recovery steady' },
    ],
    rival: 'dr-marmatite-fe',
    discriminator: {
      signal: 'pb-con-grade',
      compare: '>=',
      threshold: 65,
      evidenceKind: 'lab-sample',
      label: 'Pb concentrate Sb/As assay — elevated sulfosalts confirm tetrahedrite path',
    },
    ...causeRow('MD-053'),
    sourceCauseId: ['MD-053'],
  },
  {
    id: 'dr-marmatite-fe',
    signalPattern: 'Zn concentrate grade below spec with high lattice iron — marmatite in feed',
    diagnosis: 'High-Fe sphalerite (marmatite) — lattice iron, not removable',
    priority: priorityFor('MD-056'),
    responsible: 'superintendent',
    recommendedAction:
      'Confirm marmatite fraction in ROM; adjust mine plan or smelter terms — not fixable by flotation alone.',
    causeCategory: 'feed-ore',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-con-grade', condition: 'below-band', label: 'Zn concentrate grade below spec' },
      { signal: 'head-zn-grade', condition: 'stable', label: 'Head Zn grade steady' },
    ],
    rival: 'dr-tetrahedrite-float',
    discriminator: {
      signal: 'head-sio2',
      compare: '<=',
      threshold: 11,
      evidenceKind: 'lab-sample',
      label: 'Zn concentrate Fe assay — lattice iron confirms marmatite, not entrainment',
    },
    ...causeRow('MD-056'),
    sourceCauseId: ['MD-056'],
  },
  {
    id: 'dr-carbonate-gangue',
    signalPattern: 'Zn grade below spec + Mn/carbonate penalty risk — gangue entrainment',
    diagnosis: 'Carbonate gangue entrainment elevates Mn/carbonate impurities',
    priority: priorityFor('MD-058'),
    responsible: 'metallurgist',
    recommendedAction:
      'Tighten Zn froth stability and request carbonate/Mn panel on Zn concentrate.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-con-grade', condition: 'below-band', label: 'Zn concentrate grade below spec' },
      { signal: 'zn-rougher-froth', condition: 'above-band', label: 'Zn froth depth above band' },
    ],
    rival: 'dr-marmatite-fe',
    discriminator: {
      signal: 'grind-ph',
      compare: '<',
      threshold: 8.5,
      evidenceKind: 'lab-sample',
      label: 'Carbonate/Mn assay — elevated carbonates confirm gangue entrainment',
    },
    ...causeRow('MD-058'),
    sourceCauseId: ['MD-058'],
  },
  {
    id: 'dr-saline-penalty',
    signalPattern: 'Head As stable + penalty risk — Cl/F from saline water or fluorite gangue',
    diagnosis: 'Cl/F from saline process water or fluorite gangue',
    priority: priorityFor('MD-059'),
    responsible: 'metallurgist',
    recommendedAction:
      'Sample process water for Cl/F; bleed fresh water if recycle salinity is elevated.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-grind',
    when: [
      { signal: 'grind-ph', condition: 'excursion', label: 'Grind-circuit pH excursion' },
      { signal: 'head-as', condition: 'stable', label: 'Head As steady' },
    ],
    rival: 'dr-penalty-assay-gap',
    discriminator: {
      signal: 'head-sio2',
      compare: '>',
      threshold: 10,
      evidenceKind: 'lab-sample',
      label: 'Process water Cl/F panel — elevated halides confirm saline recycle',
    },
    ...causeRow('MD-059'),
    sourceCauseId: ['MD-059'],
  },
  {
    id: 'dr-penalty-assay-gap',
    signalPattern: 'Penalty elements unmonitored — no routine assay panel in the shift cadence',
    diagnosis: 'No routine penalty-element assay panel — detection gap',
    priority: priorityFor('MD-060'),
    responsible: 'metallurgist',
    recommendedAction:
      'Stand up a weekly penalty-element panel on Pb and Zn concentrates; route results to the rule set review queue.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-pb',
    when: [
      { signal: 'head-as', condition: 'rising', label: 'Head As rising — penalty risk unmonitored' },
      { signal: 'pb-con-grade', condition: 'stable', label: 'Pb concentrate grade steady' },
      { signal: 'zn-con-grade', condition: 'stable', label: 'Zn concentrate grade steady' },
    ],
    rival: 'dr-tetrahedrite-float',
    discriminator: {
      signal: 'head-as',
      compare: '>',
      threshold: 0.2,
      evidenceKind: 'lab-sample',
      label: 'Penalty-element panel result — any elevated Sb/As/Bi/Cd closes the detection gap',
    },
    ...causeRow('MD-060'),
    sourceCauseId: ['MD-060'],
  },
];

/** advisory-depth #327 — promoted for patterns + coverage; not in SECTION batches 1–4. */
export const ADVISORY_DEPTH_PATTERN_RULES: readonly DiagnosticRule[] = [
  {
    id: 'dr-zn-ph-low',
    signalPattern: 'Zn float pH below 10.5 + Zn recovery falling — activation insufficient',
    diagnosis: 'Zn rougher pH too low (<10.5) — activation insufficient',
    priority: priorityFor('MD-021'),
    responsible: 'metallurgist',
    recommendedAction:
      'Raise lime SP to the Zn circuit — confirm loop AIC5002 is in Auto before increasing CuSO₄.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-conditioner-ph', condition: 'below-band', label: 'Zn float pH below band' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-zn-ph-high',
    ...causeRow('MD-021'),
    sourceCauseId: ['MD-021'],
  },
  {
    id: 'dr-zn-ph-high',
    signalPattern: 'Zn float pH above 12.5 + Zn recovery falling — CuSO₄ precipitates',
    diagnosis: 'Zn rougher pH too high (>12.5) — CuSO4 precipitates',
    priority: priorityFor('MD-025'),
    responsible: 'metallurgist',
    recommendedAction:
      'Trim lime dosing to bring Zn float pH back into band before retitrating CuSO₄ solution strength.',
    causeCategory: 'metallurgical-process',
    subprocessId: 'sp-zn',
    when: [
      { signal: 'zn-conditioner-ph', condition: 'above-band', label: 'Zn float pH above band' },
      { signal: 'zn-recovery', condition: 'falling', label: 'Zn recovery falling' },
    ],
    rival: 'dr-zn-ph-low',
    ...causeRow('MD-025'),
    sourceCauseId: ['MD-025'],
  },
];
